import React from 'react';
import _ from 'lodash';
import produce from 'immer';
import type { BBCommonProps, BBModel } from 'types/story';
import { isTouch } from 'common/utils/environment';
import { COMPONENT_STATES, COMPONENT_TYPE, DISABLED_ANSWER_ATTR } from 'common/constants';
import { UNIFORM_PROPS } from 'common/constants/component-props';
import {
	INTERACTION_OWNER_ATTR,
	isIndependentAction,
	mergeBuildingBlockStates,
	shouldInheritParentHover,
} from 'common/building-blocks/link-capability';
import { META_TYPE_ATTR } from 'client/constants/common';
import SelectedComponentWatcher from 'client/components/common/SelectionHint/SelectedComponentWatcher';
import { isEditableMode, getStateDOMAttrs } from 'client/components/common/BuildingBlocks/utils/common';
import { withAnimation } from './WithAnimation';

interface WithBuildingBlockProps
	extends Pick<
		BBCommonProps,
		| 'children'
		| 'currentMediaQuery'
		| 'editableModeProps'
		| 'editorMode'
		| 'getComputedComponentProps'
		| 'in'
		| 'isCardTree'
		| 'mediaQuery'
		| 'onTransitionEnd'
		| 'resetChildrenTransition'
		| 'symbol'
		| 'transitionCSS'
		| 'type'
		| 'uiConfig'
		| '_id'
		| 'parentStates'
	> {
	Component: ReturnType<typeof withAnimation>;
	designMode?: BBModel['designMode'];
}

type WithBuildingBlockProvidedProps = {
	states: BBCommonProps['states'];
	setStates: BBCommonProps['setStates'];
	stateAttrs: BBCommonProps['stateAttrs'];
	eventListeners: BBCommonProps['eventListeners'];
	isEditableMode: BBCommonProps['isEditableMode'];
};

type WithBuildingBlockState = {
	localStates: BBCommonProps['states'];
};

class WithBuildingBlock extends React.Component<WithBuildingBlockProps, WithBuildingBlockState> {
	static displayName: string;

	constructor(props: WithBuildingBlockProps) {
		super(props);

		this.onEditableMouseEnter = this.onEditableMouseEnter.bind(this);
		this.onEditableMouseLeave = this.onEditableMouseLeave.bind(this);
		this.onEditableMouseDown = this.onEditableMouseDown.bind(this);
		this.onEditableFocus = this.onEditableFocus.bind(this);
		this.onEditableMouseDownCapture = this.onEditableMouseDownCapture.bind(this);
		this.onDoubleClickCapture = this.onDoubleClickCapture.bind(this);

		// building block current states (hover, correct, etc.)
		const initialStates: WithBuildingBlockState['localStates'] = _.reduce(
			_.omit(COMPONENT_STATES, ['DEFAULT']),
			(memo, value) => _.set(memo, value, false),
			{} as WithBuildingBlockState['localStates']
		);

		this.state = {
			localStates: initialStates,
		};
	}

	onEditableMouseEnter(event: React.MouseEvent<Element, MouseEvent>) {
		_.invoke(this.props.editableModeProps, 'eventListeners.onMouseEnter', event, this.props);
	}

	onEditableMouseLeave(event: React.MouseEvent<Element, MouseEvent>) {
		_.invoke(this.props.editableModeProps, 'eventListeners.onMouseLeave', event);
	}

	onEditableMouseDown(event: React.MouseEvent<Element, MouseEvent>) {
		_.invoke(this.props.editableModeProps, 'eventListeners.onMouseDown', event, this.props);
	}

	onEditableFocus(event: React.FocusEvent) {
		_.invoke(this.props.editableModeProps, 'eventListeners.onFocus', event, this.props);
	}

	onEditableMouseDownCapture(event: React.MouseEvent<Element, MouseEvent>) {
		_.invoke(this.props.editableModeProps, 'eventListeners.onMouseDownCapture', event, this.props);
	}

	onDoubleClickCapture(event: React.MouseEvent<Element, MouseEvent>) {
		_.invoke(this.props.editableModeProps, 'eventListeners.onDoubleClickCapture', event, this.props);
	}

	/**
	 * NOTE:
	 *   Unfortunately changing state doesn't update this.props.uiConfig.componentProps and after state changed
	 *   component will still receive computed props as it in default state.
	 *   This becomes a problem when some component inside of own code depends on "uiConfig.componentProps",
	 *   which are won't re-compute on state change.
	 *   (!) Think about, how to re-compute props for a component after his state is changed.
	 *
	 * NOTE:
	 *   Solution (!!!) -> see usage of this.props.getComputedComponentProps
	 */
	setStates: BBCommonProps['setStates'] = states => {
		this.setState(prevState => ({ localStates: { ...prevState.localStates, ...states } }));
	};

	get states(): BBCommonProps['states'] {
		return mergeBuildingBlockStates(this.state.localStates, this.props.parentStates, {
			// Answer may pass semantic states through every branch, but an action owns its own pointer hover.
			inheritParentHover: shouldInheritParentHover({ type: this.props.type, uiConfig: this.props.uiConfig }),
		}) as BBCommonProps['states'];
	}

	/**
	 * this.state.states converted as data attributes to assign onto DOM node
	 */
	get stateAttrs(): BBCommonProps['stateAttrs'] {
		if (_.isEmpty(this.states)) {
			console.error(`<${this.constructor.name} /> has no states assigned!`);
		}
		return getStateDOMAttrs(this.states);
	}

	get eventListeners(): BBCommonProps['eventListeners'] {
		const isUnselectable =
			this.props.type === COMPONENT_TYPE.CONTENT ||
			this.props.type === COMPONENT_TYPE.FLOAT_ABOVE ||
			this.props.type === COMPONENT_TYPE.FLOAT_BELOW;

		if (this.isEditableMode) {
			if (isUnselectable) {
				return undefined;
			}

			return {
				onMouseEnter: this.onEditableMouseEnter,
				onMouseLeave: this.onEditableMouseLeave,
				onMouseDown: this.onEditableMouseDown,
				onFocus: this.onEditableFocus,
				onMouseDownCapture: this.onEditableMouseDownCapture,
				onDoubleClickCapture: this.onDoubleClickCapture,
			};
		}

		const { HOVER, SELECTED, SELECTED_HOVER } = COMPONENT_STATES;
		const disabledAnswerAttr = `[${DISABLED_ANSWER_ATTR}="true"]`;
		const updateHover = (event: React.MouseEvent, hover: boolean) => {
			if (isUnselectable || isTouch || (hover && (event.target as Element).closest(disabledAnswerAttr))) return;

			const isSelected = this.states[SELECTED];
			this.setState(
				produce(draft => {
					_.set(draft, ['localStates', HOVER], hover);
					_.set(draft, ['localStates', SELECTED_HOVER], hover && isSelected);
				})
			);
		};

		// Non-editor listeners
		return {
			onMouseEnter: event => {
				updateHover(event, true);
			},
			onMouseLeave: e => {
				updateHover(e, false);
			},
		};
	}

	get isEditableMode() {
		return isEditableMode(this.props);
	}

	render() {
		const { Component, ...props } = this.props;

		const bbProps = {
			states: this.states,
			setStates: this.setStates,
			stateAttrs: this.stateAttrs,
			eventListeners: this.eventListeners,
			isEditableMode: this.isEditableMode,
			uiConfig: props.uiConfig,
		};

		/*
		 Re-compute component props for a current(not default) state

		 IMPORTANT:
		 When `this.setStates` is called, traverse-tree won't be re-called, so `uiConfig.componentProps`
		 won't be re-computed for a current(new) state and will contain values computed for a `defaultState`.
		 Next lines of code are fixing this behaviour. Checking is there is active some state that is not "default",
		 and if yes, `componentProps` will be re-computed and result assigned to BB props
		 */
		if (!bbProps.isEditableMode) {
			const stateKeys = Object.keys(bbProps.states) as (keyof BBCommonProps['states'])[];
			const currentState = stateKeys.filter(key => bbProps.states[key])?.[0];
			if (currentState) {
				bbProps.uiConfig = produce(props.uiConfig, acc => {
					acc.componentProps = props.getComputedComponentProps(currentState).value;
				});
			}
		}

		/*
		 Enhance nodeProps with data-meta-type attribute when metaType is present
		 */
		const metaType = bbProps.uiConfig.componentProps[UNIFORM_PROPS.metaType];
		const ownsActivation = isIndependentAction({ type: props.type, uiConfig: bbProps.uiConfig });
		if (metaType || ownsActivation) {
			bbProps.uiConfig = produce(bbProps.uiConfig, acc => {
				if (metaType) acc.nodeProps[META_TYPE_ATTR] = metaType;
				if (ownsActivation) acc.nodeProps[INTERACTION_OWNER_ATTR] = 'true';
			});
		}

		const providedProps = { ...props, ...bbProps };
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const { designMode: _designMode, ...componentProps } = providedProps;

		return (
			<>
				<Component {...componentProps} />

				{this.isEditableMode && props.editableModeProps && (
					<SelectedComponentWatcher
						_id={props._id}
						type={props.type}
						designMode={props.designMode}
						uiConfig={props.uiConfig}
						editableModeProps={props.editableModeProps}
						symbol={props.symbol}
						textContent={typeof props.children === 'string' ? props.children : undefined}
					/>
				)}
			</>
		);
	}
}

/**
 * HOC with basic functionality for all BuildingBlocks
 */
function withBuildingBlock<P>(Component: React.ComponentType<P>) {
	const WrappedComponent = withAnimation(Component);

	const HOC: React.FC<Omit<WithBuildingBlockProps, 'Component' | keyof WithBuildingBlockProvidedProps>> = props => (
		<WithBuildingBlock {...props} Component={WrappedComponent}>
			{props.children}
		</WithBuildingBlock>
	);

	HOC.displayName = `withBuildingBlock(${Component.displayName || Component.name})`;

	return HOC;
}

export { withBuildingBlock };
